import { forEach, isElement, isNil, isEqual } from "lodash";
import Breakpoint from "../../01-helpers/breakpoint-util";
import Hamburger from "../../04-components/buttons/hamburger";
import Login from "./login";
import MainNav from "./main-nav";
import Popover from "../../04-components/popover";
import Advertisement from "../../04-components/advertisements";
import {
    subscribe
} from "../../01-helpers/event-manager";
import {
    BREAKPOINT_CHANGE,
    HAMBURGER_CLICKED,
    DROPDOWN_CLICKED
} from "../../02-const/event-names";

const
    moduleName = 'header',
    baseCssClass = `js-${moduleName}`,

    propertyNames = {
        baseElement: Symbol('baseElement'),
        mobileTemplate: Symbol('mobileTemplate'),
        tabletTemplate: Symbol('tabletTemplate'),
        desktopTemplate: Symbol('desktopTemplate')
    },

    selectors = {
        base: `.${baseCssClass}`,
        hamburger: '.js-hamburger',
        search: '.js-search',
        menu: '.js-menu',
        login: '.js-login',
        mainNavigation: '.js-main-navigation',
        overlay: '.js-overlay',
        popover: '.js-popover',
        ad: '.js-ad'
    },

    css = {
        collapsed: 'collapsed'
    },

    attributeNames = {
        dataMobileTemplateId: "data-header-mobile-template-id",
        dataTabletTemplateId: "data-header-tablet-template-id",
        dataDesktopTemplateId: "data-header-desktop-template-id"
    };

/**
 * bind events
 */
function bindEvents() {
    subscribe(BREAKPOINT_CHANGE, breakpointChanged.bind(this));
    subscribe(HAMBURGER_CLICKED, hamburgerClicked.bind(this));
    subscribe(DROPDOWN_CLICKED, dropdownClicked.bind(this));
}

/**
 * triggers on breakpoint change event.
 */
function breakpointChanged() {
    init.call(this);
}

function hamburgerClicked(evt) {
    const clickedId = evt.detail.hamburgerId;
    const hamburgerId = this.hamburger.id;
    if (isEqual(clickedId, hamburgerId)) {
        toggleMenu.call(this);
    }
}

function dropdownClicked() {
    if (!isNil(this.popover)) {
        this.popover.hide();
    }
}

/**
 * init
 */
function init() {
    buildHeader.call(this);
    initLogin.call(this);
    initMenu.call(this);
    initMainNavigation.call(this);
    initPopover.call(this);

    if (!Breakpoint.isDesk()) {
        initHamburger.call(this);
        initSearchButton.call(this);
        initOverlay.call(this);
    }

    initAds.call(this);
}

/**
 * returns html template for given device.
 */
function getTemplate(attributeId) {
    // get base element
    const baseElement = propertyNames.baseElement;
    if (!baseElement.hasAttribute(attributeId)) {
        throw `Unable to get attribute ${attributeId} for header component`;
    }

    // get the value from the attribute 'data-header-mobile-template-id' or 'data-header-dekstop-template-id' (depenidng on attributeId param).
    // The value is the id of an html element witch contains the html for the template.
    const templateId = baseElement.getAttribute(attributeId);

    // get html-element based on templateId and return it's inner html as the template.
    return document.getElementById(templateId).innerHTML;
}

/**
 * depending on device, get right template and build it.
 */
function buildHeader() {
    const baseElement = propertyNames.baseElement;
    let template = propertyNames.mobileTemplate;
    if (Breakpoint.isLap()) {
        template = propertyNames.tabletTemplate;
    } else if (Breakpoint.isDesk()) {
        template = propertyNames.desktopTemplate;
    }

    baseElement.innerHTML = template;
}

function initHamburger() {
    if (!isNil(this.hamburger)) {
        this.hamburger.destroy();
    }
    const hamburgerElement = propertyNames.baseElement.querySelector(selectors.hamburger);
    this.hamburger = new Hamburger(hamburgerElement);
}

function initLogin() {
    if (!isNil(this.login)) {
        this.login.destroy();
    }

    try {
        const loginElement = propertyNames.baseElement.querySelector(selectors.login);
        this.login = new Login(loginElement);
    }
    catch (e) {
        console.error(e);
    }
}

function initMenu() {
    this.menu = propertyNames.baseElement.querySelector(selectors.menu);
}

function initMainNavigation() {
    const mainNavElement = propertyNames.baseElement.querySelector(selectors.mainNavigation);
    this.mainNavigation = new MainNav(mainNavElement);
}

function initSearchButton() {
    if (!isNil(this.searchButton)) {
        this.searchButton.removeEventListener('click', searchClicked.bind(this));
    }
    this.searchButton = propertyNames.baseElement.querySelector(selectors.search);

    if (isNil(this.searchButton)) { return; }

    this.searchButton.addEventListener('click', searchClicked.bind(this));
}

function initOverlay() {
    if (!isNil(this.overlayClose)) {
        this.overlayClose.removeEventListener('click', closeOverlay.bind(this));
    }
    this.overlay = propertyNames.baseElement.querySelector(selectors.overlay);
    this.overlayClose = propertyNames.baseElement.querySelector('.js-overlay-close');
    this.overlayClose.addEventListener('click', closeOverlay.bind(this));
}

function initPopover() {
    if (!isNil(this.popover)) {
        this.popover.destroy();
        this.popover = null;
    }

    const popoverElement = propertyNames.baseElement.querySelector(selectors.popover);
    if (isElement(popoverElement)) {
        this.popover = new Popover(popoverElement);
    }
}

function initAds() {
    forEach(propertyNames.baseElement.querySelectorAll(selectors.ad), adItem => {
        new Advertisement(adItem);
    });
}

function toggleMenu() {
    const action = this.menu.classList.contains(css.collapsed) ? 'remove' : 'add';
    this.menu.classList[action](css.collapsed);

    if (!this.menu.classList.contains(css.collapsed)) {
        this.menu.querySelector(`.c-header__menu-bottom`).scrollTop = 0;
    }

    if (!isNil(this.login)) {
        this.login.close();
    }
    if (!isNil(this.popover)) {
        this.popover.hide();
    }
}

function searchClicked(evt) {
    this.overlay.classList.remove('hide');
}

function closeOverlay() {
    this.overlay.classList.add('hide');
}


/**
 * JS module for <header> tag in golf.nl platform
 */
class Header {
    /**
     * @memberof Header
     */
    constructor(baseElement) {
        if (!isElement(baseElement)) {
            throw "Unable to create instance of header, no base element has been provided";
        }

        // Store the base element for this instance.
        propertyNames.baseElement = baseElement;

        // Store the templates for mobile and desktop.
        propertyNames.mobileTemplate = getTemplate.call(this, attributeNames.dataMobileTemplateId);
        propertyNames.tabletTemplate = getTemplate.call(this, attributeNames.dataTabletTemplateId);
        propertyNames.desktopTemplate = getTemplate.call(this, attributeNames.dataDesktopTemplateId);

        // Bind events
        bindEvents.call(this);

        init.call(this);
    }
}
/*
 * search for all js-header classes in the project and apply this module.
 */
forEach(document.querySelectorAll(selectors.base), headerItem => {
    new Header(headerItem);
});

export default Header;
